import { useState, useEffect, useRef } from 'react'
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi'

/**
 * ReviewsCarousel Component
 * Displays customer reviews in an auto-sliding carousel format
 * with smooth transitions and navigation controls
 */
export default function ReviewsCarousel({
  reviews,
  settings,
  showReviewForm = true,
  onReviewSubmit
}) {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isTransitioning, setIsTransitioning] = useState(false)
  const [isPaused, setIsPaused] = useState(false)
  const autoPlayIntervalRef = useRef(null)

  // Validate inputs
  if (!reviews || reviews.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        No reviews yet
      </div>
    )
  }

  const carouselSettings = settings || {
    autoPlay: true,
    interval: 8000, // Longer interval for reviews
    transitionSpeed: 600,
    showIndicators: true,
    showNavigation: true,
    reviewsPerView: 1
  }

  // Auto-play functionality
  useEffect(() => {
    if (!carouselSettings.autoPlay || reviews.length <= 1 || isPaused) {
      if (autoPlayIntervalRef.current) {
        clearInterval(autoPlayIntervalRef.current)
      }
      return
    }

    autoPlayIntervalRef.current = setInterval(() => {
      setCurrentIndex(prev => (prev + 1) % reviews.length)
    }, carouselSettings.interval || 8000)

    return () => {
      if (autoPlayIntervalRef.current) {
        clearInterval(autoPlayIntervalRef.current)
      }
    }
  }, [reviews.length, carouselSettings.autoPlay, carouselSettings.interval, isPaused])

  const handlePrevious = () => {
    setCurrentIndex(prev => (prev - 1 + reviews.length) % reviews.length)
    setIsTransitioning(true)
    setTimeout(() => setIsTransitioning(false), carouselSettings.transitionSpeed || 600)
  }

  const handleNext = () => {
    setCurrentIndex(prev => (prev + 1) % reviews.length)
    setIsTransitioning(true)
    setTimeout(() => setIsTransitioning(false), carouselSettings.transitionSpeed || 600)
  }

  const handleDotClick = (index) => {
    setCurrentIndex(index)
    setIsTransitioning(true)
    setTimeout(() => setIsTransitioning(false), carouselSettings.transitionSpeed || 600)
  }

  const currentReview = reviews[currentIndex]
  const transitionDuration = carouselSettings.transitionSpeed || 600

  const getReviewCardHeightClass = (review) => {
    const textLength = getReviewText(review).length

    if (textLength > 380) return 'min-h-[480px] sm:min-h-[560px]'
    if (textLength > 260) return 'min-h-[420px] sm:min-h-[500px]'
    if (textLength > 140) return 'min-h-[340px] sm:min-h-[400px]'
    return 'min-h-[280px] sm:min-h-[340px]'
  }

  const hasValidRating = (review) => {
    const ratingValue = Number(review?.rating)
    return Number.isFinite(ratingValue) && ratingValue >= 1 && ratingValue <= 5
  }

  const getReviewText = (review) => {
    const text = (
      review?.comment ||
      review?.message ||
      review?.reviewMessage ||
      review?.text ||
      review?.review ||
      ''
    )
    return text?.toString().trim()
  }

  const getAverageRating = (items) => {
    const ratedReviews = items.filter(hasValidRating)
    if (ratedReviews.length === 0) return null

    const total = ratedReviews.reduce((sum, review) => sum + Number(review.rating), 0)
    return (total / ratedReviews.length).toFixed(1)
  }

  return (
    <div className="w-full">
      {/* Reviews Carousel Container */}
      <div
        className={`relative bg-white rounded-lg p-4 sm:p-6 shadow-lg overflow-hidden transition-all duration-300 ${getReviewCardHeightClass(currentReview)}`}
        onMouseEnter={() => carouselSettings.autoPlay && setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        {/* Reviews Display */}
        <div className="relative w-full h-full">
          {reviews.map((review, idx) => {
            const reviewText = getReviewText(review)
            return (
              <div
                key={review._id || idx}
                className="absolute inset-0 w-full h-full transition-opacity duration-600 ease-in-out overflow-visible"
                style={{
                  opacity: idx === currentIndex ? 1 : 0,
                  transitionDuration: `${transitionDuration}ms`,
                  zIndex: idx === currentIndex ? 1 : 0
                }}
              >
                <div className="flex h-full min-h-full items-start justify-center px-1 sm:px-4 py-4 overflow-visible">
                  {/* Review Content */}
                  <div className="w-full min-h-0 text-center overflow-visible pb-10 sm:pb-12 flex flex-col items-center">
                    {/* Star Rating */}
                    {(() => {
                      if (!hasValidRating(review)) return null

                      const ratingValue = Number(review.rating)
                      const normalizedRating = Math.min(5, Math.max(1, Math.round(ratingValue)))
                      return (
                        <div className="flex flex-wrap justify-center items-center gap-1 sm:gap-2 mb-4 px-2 text-center">
                          <div className="text-xl sm:text-2xl leading-none">
                            <span className="text-yellow-400">
                              {'★'.repeat(normalizedRating)}
                            </span>
                            <span className="text-gray-300">
                              {'★'.repeat(5 - normalizedRating)}
                            </span>
                          </div>
                          <div className="text-xs sm:text-sm text-gray-600 whitespace-nowrap">
                            ({normalizedRating}/5)
                          </div>
                        </div>
                      )
                    })()}

                    {/* Review Text */}
                    {reviewText ? (
                      <blockquote
                        className="w-full max-w-3xl mx-auto mb-6 text-sm sm:text-base lg:text-lg text-gray-700 italic leading-6 sm:leading-7 whitespace-pre-wrap break-words px-2 sm:px-3 pb-1"
                        style={{ overflowWrap: 'anywhere', wordBreak: 'break-word', hyphens: 'auto' }}
                      >
                        {reviewText}
                      </blockquote>
                    ) : (
                      <div className="text-sm text-gray-500 italic mb-6">No review message available.</div>
                    )}

                    {/* Reviewer Info */}
                    <div className="text-center px-2 pb-2">
                      <div className="font-semibold text-gray-900 text-base sm:text-lg break-words">{review.visitorName || 'Anonymous'}</div>
                      <div className="text-xs sm:text-sm text-gray-500 mt-1 break-words">
                        {new Date(review.createdAt).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )
          })}
        </div>

        {/* Navigation Arrows */}
        {carouselSettings.showNavigation !== false && reviews.length > 1 && (
          <>
            <button
              onClick={handlePrevious}
              className="absolute left-4 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white text-gray-700 p-2 rounded-full shadow-md transition-all duration-200 hover:scale-110"
              aria-label="Previous review"
              type="button"
            >
              <FiChevronLeft className="w-5 h-5" />
            </button>

            <button
              onClick={handleNext}
              className="absolute right-4 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white text-gray-700 p-2 rounded-full shadow-md transition-all duration-200 hover:scale-110"
              aria-label="Next review"
              type="button"
            >
              <FiChevronRight className="w-5 h-5" />
            </button>
          </>
        )}

        {/* Indicator Dots */}
        {carouselSettings.showIndicators !== false && reviews.length > 1 && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 flex gap-2">
            {reviews.map((_, idx) => (
              <button
                key={idx}
                onClick={() => handleDotClick(idx)}
                className={`transition-all duration-300 rounded-full ${
                  idx === currentIndex
                    ? 'bg-blue-600 w-3 h-3'
                    : 'bg-gray-300 hover:bg-gray-400 w-2.5 h-2.5'
                }`}
                aria-label={`Go to review ${idx + 1}`}
                type="button"
              />
            ))}
          </div>
        )}

        {/* Review Counter */}
        {reviews.length > 1 && (
          <div className="absolute top-4 right-4 z-10 bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm font-medium">
            {currentIndex + 1} / {reviews.length}
          </div>
        )}

        {/* Auto-play indicator */}
        {carouselSettings.autoPlay && reviews.length > 1 && (
          <div className="absolute top-4 left-4 z-10 flex items-center gap-2 bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm">
            <div className={`w-2 h-2 rounded-full ${isPaused ? 'bg-yellow-400' : 'bg-green-400'} animate-pulse`} />
            <span>{isPaused ? 'Paused' : 'Auto'}</span>
          </div>
        )}
      </div>

      {/* Review Statistics */}
      {reviews.length > 0 && (
        <div className="mt-4 text-center text-sm text-gray-600">
          <span className="font-medium">{reviews.length}</span> customer review{reviews.length !== 1 ? 's' : ''}
          {getAverageRating(reviews) && (
            <>
              {' • '}
              Average rating: <span className="font-medium text-yellow-600">{getAverageRating(reviews)} ★</span>
            </>
          )}
        </div>
      )}
    </div>
  )
}


// import { useState, useEffect, useRef } from 'react'
// import { FiChevronLeft, FiChevronRight } from 'react-icons/fi'

// /**
//  * ReviewsCarousel Component
//  * Displays customer reviews in an auto-sliding carousel format
//  * with smooth transitions and navigation controls
//  */
// export default function ReviewsCarousel({
//   reviews,
//   settings,
//   showReviewForm = true,
//   onReviewSubmit
// }) {
//   const [currentIndex, setCurrentIndex] = useState(0)
//   const [isTransitioning, setIsTransitioning] = useState(false)
//   const [isPaused, setIsPaused] = useState(false)
//   const autoPlayIntervalRef = useRef(null)

//   // Validate inputs
//   if (!reviews || reviews.length === 0) {
//     return (
//       <div className="text-center py-8 text-gray-500">
//         No reviews yet
//       </div>
//     )
//   }

//   const carouselSettings = settings || {
//     autoPlay: true,
//     interval: 8000, // Longer interval for reviews
//     transitionSpeed: 600,
//     showIndicators: true,
//     showNavigation: true,
//     reviewsPerView: 1
//   }

//   // Auto-play functionality
//   useEffect(() => {
//     if (!carouselSettings.autoPlay || reviews.length <= 1 || isPaused) {
//       if (autoPlayIntervalRef.current) {
//         clearInterval(autoPlayIntervalRef.current)
//       }
//       return
//     }

//     autoPlayIntervalRef.current = setInterval(() => {
//       setCurrentIndex(prev => (prev + 1) % reviews.length)
//     }, carouselSettings.interval || 8000)

//     return () => {
//       if (autoPlayIntervalRef.current) {
//         clearInterval(autoPlayIntervalRef.current)
//       }
//     }
//   }, [reviews.length, carouselSettings.autoPlay, carouselSettings.interval, isPaused])

//   const handlePrevious = () => {
//     setCurrentIndex(prev => (prev - 1 + reviews.length) % reviews.length)
//     setIsTransitioning(true)
//     setTimeout(() => setIsTransitioning(false), carouselSettings.transitionSpeed || 600)
//   }

//   const handleNext = () => {
//     setCurrentIndex(prev => (prev + 1) % reviews.length)
//     setIsTransitioning(true)
//     setTimeout(() => setIsTransitioning(false), carouselSettings.transitionSpeed || 600)
//   }

//   const handleDotClick = (index) => {
//     setCurrentIndex(index)
//     setIsTransitioning(true)
//     setTimeout(() => setIsTransitioning(false), carouselSettings.transitionSpeed || 600)
//   }

//   const currentReview = reviews[currentIndex]
//   const transitionDuration = carouselSettings.transitionSpeed || 600

//   const getReviewText = (review) => {
//     const text = (
//       review?.comment ||
//       review?.message ||
//       review?.reviewMessage ||
//       review?.text ||
//       review?.review ||
//       ''
//     )
//     return text?.toString().trim()
//   }

//   return (
//     <div className="w-full">
//       {/* Reviews Carousel Container */}
//       <div
//         className="relative bg-white rounded-lg p-6 shadow-lg min-h-[240px] overflow-hidden"
//         onMouseEnter={() => carouselSettings.autoPlay && setIsPaused(true)}
//         onMouseLeave={() => setIsPaused(false)}
//       >
//         {/* Reviews Display */}
//         <div className="relative w-full h-full">
//           {reviews.map((review, idx) => {
//             const reviewText = getReviewText(review)
//             return (
//               <div
//                 key={review._id || idx}
//                 className="absolute inset-0 w-full h-full transition-opacity duration-600 ease-in-out"
//                 style={{
//                   opacity: idx === currentIndex ? 1 : 0,
//                   transitionDuration: `${transitionDuration}ms`,
//                   zIndex: idx === currentIndex ? 1 : 0
//                 }}
//               >
//                 <div className="flex flex-col h-full justify-center px-2 sm:px-6">
//                   {/* Review Content */}
//                   <div className="text-center">
//                     {/* Star Rating */}
//                     {(() => {
//                       const ratingValue = Number(review.rating)
//                       const hasRating = Number.isFinite(ratingValue) && ratingValue >= 1 && ratingValue <= 5
//                       if (!hasRating) return null

//                       const normalizedRating = Math.min(5, Math.max(1, Math.round(ratingValue)))
//                       return (
//                         <div className="flex justify-center items-center gap-2 mb-4 flex-wrap">
//                           <div className="text-2xl">
//                             <span className="text-yellow-400">
//                               {'★'.repeat(normalizedRating)}
//                             </span>
//                             <span className="text-gray-300">
//                               {'★'.repeat(5 - normalizedRating)}
//                             </span>
//                           </div>
//                           <div className="text-sm text-gray-600">
//                             ({normalizedRating}/5)
//                           </div>
//                         </div>
//                       )
//                     })()}

//                     {/* Review Text */}
//                     {reviewText ? (
//                       <blockquote className="text-base sm:text-lg text-gray-700 italic mb-6 max-w-3xl mx-auto leading-relaxed whitespace-pre-wrap break-words">
//                         {reviewText}
//                       </blockquote>
//                     ) : (
//                       <div className="text-sm text-gray-500 italic mb-6">No review message available.</div>
//                     )}

//                     {/* Reviewer Info */}
//                     <div className="text-center">
//                       <div className="font-semibold text-gray-900 text-lg truncate">{review.visitorName || 'Anonymous'}</div>
//                       <div className="text-sm text-gray-500 mt-1">
//                         {new Date(review.createdAt).toLocaleDateString('en-US', {
//                           year: 'numeric',
//                           month: 'long',
//                           day: 'numeric'
//                         })}
//                       </div>
//                     </div>
//                   </div>
//                 </div>
//               </div>
//             )
//           })}
//         </div>

//         {/* Navigation Arrows */}
//         {carouselSettings.showNavigation !== false && reviews.length > 1 && (
//           <>
//             <button
//               onClick={handlePrevious}
//               className="absolute left-4 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white text-gray-700 p-2 rounded-full shadow-md transition-all duration-200 hover:scale-110"
//               aria-label="Previous review"
//               type="button"
//             >
//               <FiChevronLeft className="w-5 h-5" />
//             </button>

//             <button
//               onClick={handleNext}
//               className="absolute right-4 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white text-gray-700 p-2 rounded-full shadow-md transition-all duration-200 hover:scale-110"
//               aria-label="Next review"
//               type="button"
//             >
//               <FiChevronRight className="w-5 h-5" />
//             </button>
//           </>
//         )}

//         {/* Indicator Dots */}
//         {carouselSettings.showIndicators !== false && reviews.length > 1 && (
//           <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 flex gap-2">
//             {reviews.map((_, idx) => (
//               <button
//                 key={idx}
//                 onClick={() => handleDotClick(idx)}
//                 className={`transition-all duration-300 rounded-full ${
//                   idx === currentIndex
//                     ? 'bg-blue-600 w-3 h-3'
//                     : 'bg-gray-300 hover:bg-gray-400 w-2.5 h-2.5'
//                 }`}
//                 aria-label={`Go to review ${idx + 1}`}
//                 type="button"
//               />
//             ))}
//           </div>
//         )}

//         {/* Review Counter */}
//         {reviews.length > 1 && (
//           <div className="absolute top-4 right-4 z-10 bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm font-medium">
//             {currentIndex + 1} / {reviews.length}
//           </div>
//         )}

//         {/* Auto-play indicator */}
//         {carouselSettings.autoPlay && reviews.length > 1 && (
//           <div className="absolute top-4 left-4 z-10 flex items-center gap-2 bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm">
//             <div className={`w-2 h-2 rounded-full ${isPaused ? 'bg-yellow-400' : 'bg-green-400'} animate-pulse`} />
//             <span>{isPaused ? 'Paused' : 'Auto'}</span>
//           </div>
//         )}
//       </div>

//       {/* Review Statistics */}
//       {reviews.length > 0 && (
//         <div className="mt-4 text-center text-sm text-gray-600">
//           <span className="font-medium">{reviews.length}</span> customer review{reviews.length !== 1 ? 's' : ''}
//           {reviews.filter(r => r.rating).length > 0 && (
//             <>
//               {' • '}
//               Average rating: <span className="font-medium text-yellow-600">
//                 {((reviews.filter(r => r.rating).reduce((sum, r) => sum + r.rating, 0)) / reviews.filter(r => r.rating).length).toFixed(1)} ★
//               </span>
//             </>
//           )}
//         </div>
//       )}
//     </div>
//   )
// }